import { reduce, isEmpty } from "lodash"
import { useEffect, useCallback } from "react"

const downloadCallbacks: Record<string, () => Subscriber> = {}

interface Subscriber {
  name: string
  combineSubscribers?: boolean
  data: {
    rows: string[][]
    headers: string[]
  }
}

function sortSubscribers(subscribers: Record<string, Subscriber>, order: Record<string, number>) {
  if (!order) {
    return Object.keys(subscribers)
  }

  return Object.keys(subscribers).toSorted((a, b) => {
    // If the sheet is not in the order, it will be sorted last
    const orderA = order[a.split(".")[0]] ?? 100
    const orderB = order[b.split(".")[0]] ?? 100
    if (orderA === orderB) {
      // Secondary sort: alphabetical
      return a.localeCompare(b)
    }
    return orderA - orderB
  })
}

function getDownloadSubscribers(): Record<string, Subscriber> {
  return reduce(downloadCallbacks, (result: Record<string, Subscriber>, callback, key) => ({
    ...result,
    [key]: callback(),
  }), {})
}

export function useDownloadSubscribers(key: string, callback: () => Subscriber) {
  useEffect(() => {
    if (key) {
      downloadCallbacks[key] = callback

      return () => {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete downloadCallbacks[key]
      }
    }
  }, [key, callback])
}

export interface DownloadSheet {
  name: string
  rows: string[][]
}

export function useReduceDownloadDataFromSubscribers(order: Record<string, number>) {
  return useCallback(() => {
    const subscribers = getDownloadSubscribers()
    if (isEmpty(subscribers)) {
      return null
    }

    // In some cases, we want to combine the data from multiple subscribers into a single sheet
    const combinedSubscribers = Object.entries(subscribers).reduce((acc: Record<string, Subscriber>, [key, subscriber]) => {
      if (!subscriber.combineSubscribers) {
        acc[key] = subscriber
        return acc
      }
      const [combinationKey] = key.split(".")
      if (!acc[combinationKey]) {
        acc[combinationKey] = subscriber
      } else {
        // Include a blank row between each subscriber to separate them in the excel sheet
        acc[combinationKey].data.rows.push([], ...subscriber.data.rows)
      }
      return acc
    }, {})

    const sortedSubscribers = sortSubscribers(combinedSubscribers, order)

    const sheets = sortedSubscribers.reduce((acc: DownloadSheet[], key) => {
      const { data, name } = combinedSubscribers[key]
      if (isEmpty(data)) {
        return acc
      }
      const { headers, rows } = data
      const newSheet = {
        name,
        rows: [headers, ...rows],
      }
      return [...acc, newSheet]
    }, [])
    return isEmpty(sheets) ? null : { sheets }
  }, [order])
}
